<template>
  <div id="app" :class="{ 'no-bottom-blur': !showBottomNav }">
    <router-view v-slot="{ Component }">
      <transition name="page" mode="out-in">
        <component :is="Component" :key="$route.path" />
      </transition>
    </router-view>

    <!-- Bottom Navigation - Visible on main pages -->
    <BottomNav v-if="showBottomNav" />

    <!-- All Modals -->
    <LoginModal />
    <OtpModal />
    <OffnetModal />
    <SubscribeModal />
    <UnsubscribeModal />
    <SubscribeSuccessModal />
    <SubscribeErrorModal />

    <!-- Loading Overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loader">
        <div class="loader-ring"></div>
        <div class="loader-ring"></div>
        <div class="loader-dot"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import BottomNav from './components/BottomNav.vue'
import LoginModal from './components/LoginModal.vue'
import OtpModal from './components/OtpModal.vue'
import OffnetModal from './components/OffnetModal.vue'
import SubscribeModal from './components/SubscribeModal.vue'
import UnsubscribeModal from './components/UnsubscribeModal.vue'
import SubscribeSuccessModal from './components/SubscribeSuccessModal.vue'
import SubscribeErrorModal from './components/SubscribeErrorModal.vue'

const HIDDEN_NAV_ROUTES = ['/onboarding', '/otp-verification', '/trivia', '/select-teams']

export default {
  name: 'App',
  components: {
    BottomNav,
    LoginModal,
    OtpModal,
    OffnetModal,
    SubscribeModal,
    UnsubscribeModal,
    SubscribeSuccessModal,
    SubscribeErrorModal
  },
  computed: {
    ...mapState(['isLoading']),
    showBottomNav() {
      return !HIDDEN_NAV_ROUTES.includes(this.$route.path)
    }
  },
  methods: {
    ...mapActions(['checkSession'])
  },
  mounted() {
    this.checkSession()
  }
}
</script>

<style lang="scss">
@import '@/assets/styles/tokens';

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

body {
  font-family: $font-body;
  background: linear-gradient(180deg, $brand-dark 0%, $brand-dark-soft 15%, lighten($brand-dark-soft, 8%) 50%, $gray-100 100%);
  background-attachment: fixed;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

#app {
  max-width: $app-max-width;
  margin: 0 auto;
  min-height: 100vh;
  background: $white;
  position: relative;
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.03),
    0 20px 60px -12px rgba(0, 0, 0, 0.2),
    0 0 80px -20px rgba($brand-red, 0.06);
}

#app::after {
  content: '';
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: $app-max-width;
  height: 85px;
  background: linear-gradient(to top, rgba($white, 0.7) 0%, rgba($white, 0.2) 40%, transparent 100%);
  backdrop-filter: blur(12px) saturate(200%);
  -webkit-backdrop-filter: blur(12px) saturate(200%);
  pointer-events: none;
  z-index: 999;
  mask-image: linear-gradient(to top, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.7) 50%, rgba(0, 0, 0, 0) 100%);
  -webkit-mask-image: linear-gradient(to top, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.7) 50%, rgba(0, 0, 0, 0) 100%);
}

#app.no-bottom-blur::after {
  display: none;
}

.page-enter-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.page-leave-active {
  transition: opacity 0.15s ease;
}

.page-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.page-leave-to {
  opacity: 0;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba($brand-dark, 0.55);
  backdrop-filter: blur(12px) saturate(120%);
  -webkit-backdrop-filter: blur(12px) saturate(120%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: overlayIn 0.25s ease;
}

.loader {
  position: relative;
  width: 48px;
  height: 48px;
}

.loader-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 3px solid transparent;
}

.loader-ring:nth-child(1) {
  border-top-color: $brand-red;
  animation: loaderSpin 1s cubic-bezier(0.5, 0, 0.5, 1) infinite;
  box-shadow: 0 0 20px rgba($brand-red, 0.3);
}

.loader-ring:nth-child(2) {
  border-right-color: rgba($brand-red, 0.35);
  animation: loaderSpin 1s cubic-bezier(0.5, 0, 0.5, 1) infinite 0.15s;
}

.loader-dot {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 8px;
  height: 8px;
  margin: -4px 0 0 -4px;
  border-radius: 50%;
  background: $brand-red;
  box-shadow: 0 0 12px $brand-red-glow;
  animation: loaderPulse 1s ease-in-out infinite;
}

@keyframes loaderSpin {
  to { transform: rotate(360deg); }
}

@keyframes loaderPulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(0.6); opacity: 0.4; }
}

@keyframes overlayIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
